/**
 * Viewport Detection and Mobile-First Responsive Behaviors
 * Provides comprehensive viewport management and responsive utilities
 */

export interface ViewportInfo {
  width: number;
  height: number;
  aspectRatio: number;
  orientation: 'portrait' | 'landscape';
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  devicePixelRatio: number;
  safeAreaInsets: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface ResponsiveConfig {
  breakpoints: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
  debounceDelay: number;
  enableSafeArea: boolean;
  enableVisualViewport: boolean;
}

export type ViewportChangeCallback = (viewport: ViewportInfo) => void;

export class ViewportDetector {
  private config: ResponsiveConfig;
  private callbacks: Set<ViewportChangeCallback> = new Set();
  private currentViewport: ViewportInfo;
  private resizeObserver: ResizeObserver | null = null;
  private visualViewport: VisualViewport | null = null;
  private debounceTimer: number | null = null;

  constructor(config: Partial<ResponsiveConfig> = {}) {
    this.config = {
      breakpoints: {
        xs: 320,
        sm: 480,
        md: 600,
        lg: 840,
        xl: 1200,
      },
      debounceDelay: 100,
      enableSafeArea: true,
      enableVisualViewport: true,
      ...config,
    };

    this.currentViewport = this.calculateViewport();
    this.initializeListeners();
  }

  /**
   * Calculate current viewport information
   */
  private calculateViewport(): ViewportInfo {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const aspectRatio = width / height;
    const orientation = width > height ? 'landscape' : 'portrait';
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Visual viewport support for mobile browsers
    let visualWidth = width;
    let visualHeight = height;
    if (this.config.enableVisualViewport && window.visualViewport) {
      visualWidth = window.visualViewport.width;
      visualHeight = window.visualViewport.height;
    }

    const breakpoint = this.getBreakpoint(width);
    const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
    const isTablet = breakpoint === 'md';
    const isDesktop = breakpoint === 'lg' || breakpoint === 'xl';

    const safeAreaInsets = this.config.enableSafeArea
      ? this.getSafeAreaInsets()
      : {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        };

    return {
      width: visualWidth,
      height: visualHeight,
      aspectRatio,
      orientation,
      isMobile,
      isTablet,
      isDesktop,
      breakpoint,
      devicePixelRatio,
      safeAreaInsets,
    };
  }

  /**
   * Get current breakpoint based on width
   */
  private getBreakpoint(width: number): 'xs' | 'sm' | 'md' | 'lg' | 'xl' {
    if (width < this.config.breakpoints.sm) return 'xs';
    if (width < this.config.breakpoints.md) return 'sm';
    if (width < this.config.breakpoints.lg) return 'md';
    if (width < this.config.breakpoints.xl) return 'lg';
    return 'xl';
  }

  /**
   * Get safe area insets for devices with notches/rounded corners
   */
  private getSafeAreaInsets(): {
    top: number;
    right: number;
    bottom: number;
    left: number;
  } {
    const style = getComputedStyle(document.documentElement);

    const getValue = (property: string): number => {
      const value = style.getPropertyValue(property);
      return value ? parseInt(value.replace('px', '')) : 0;
    };

    return {
      top:
        getValue('--safe-area-inset-top') ||
        getValue('env(safe-area-inset-top)') ||
        0,
      right:
        getValue('--safe-area-inset-right') ||
        getValue('env(safe-area-inset-right)') ||
        0,
      bottom:
        getValue('--safe-area-inset-bottom') ||
        getValue('env(safe-area-inset-bottom)') ||
        0,
      left:
        getValue('--safe-area-inset-left') ||
        getValue('env(safe-area-inset-left)') ||
        0,
    };
  }

  /**
   * Initialize event listeners
   */
  private initializeListeners(): void {
    // Standard resize listener
    window.addEventListener('resize', this.handleResize.bind(this));
    window.addEventListener(
      'orientationchange',
      this.handleOrientationChange.bind(this)
    );

    // Visual viewport listener for mobile
    if (this.config.enableVisualViewport && window.visualViewport) {
      this.visualViewport = window.visualViewport;
      this.visualViewport.addEventListener(
        'resize',
        this.handleViewportResize.bind(this)
      );
      this.visualViewport.addEventListener(
        'scroll',
        this.handleViewportScroll.bind(this)
      );
    }

    // ResizeObserver for more accurate detection
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(
        this.handleResizeObserver.bind(this)
      );
      this.resizeObserver.observe(document.documentElement);
    }
  }

  /**
   * Handle window resize
   */
  private handleResize(): void {
    this.debounceViewportUpdate();
  }

  /**
   * Handle orientation change
   */
  private handleOrientationChange(): void {
    // Add delay for orientation change to complete
    setTimeout(() => {
      this.updateViewport();
    }, 200);
  }

  /**
   * Handle visual viewport resize
   */
  private handleViewportResize(): void {
    this.debounceViewportUpdate();
  }

  /**
   * Handle visual viewport scroll
   */
  private handleViewportScroll(): void {
    // Only update if height changed significantly (keyboard)
    const newHeight = this.visualViewport?.height || window.innerHeight;
    if (Math.abs(newHeight - this.currentViewport.height) > 100) {
      this.debounceViewportUpdate();
    }
  }

  /**
   * Handle ResizeObserver updates
   */
  private handleResizeObserver(): void {
    this.debounceViewportUpdate();
  }

  /**
   * Debounce viewport updates
   */
  private debounceViewportUpdate(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.updateViewport();
    }, this.config.debounceDelay);
  }

  /**
   * Update viewport and notify listeners
   */
  private updateViewport(): void {
    const newViewport = this.calculateViewport();

    // Only trigger callbacks if something significant changed
    if (this.hasSignificantChange(this.currentViewport, newViewport)) {
      this.currentViewport = newViewport;
      this.notifyCallbacks();
    }
  }

  /**
   * Check if viewport change is significant enough to trigger callbacks
   */
  private hasSignificantChange(old: ViewportInfo, new_: ViewportInfo): boolean {
    return (
      old.breakpoint !== new_.breakpoint ||
      old.orientation !== new_.orientation ||
      Math.abs(old.width - new_.width) > 50 ||
      Math.abs(old.height - new_.height) > 100
    );
  }

  /**
   * Notify all registered callbacks
   */
  private notifyCallbacks(): void {
    this.callbacks.forEach((callback) => {
      try {
        callback(this.currentViewport);
      } catch {
        // Silently ignore callback errors to prevent disruption
      }
    });
  }

  /**
   * Subscribe to viewport changes
   */
  public subscribe(callback: ViewportChangeCallback): () => void {
    this.callbacks.add(callback);

    // Immediately call with current viewport
    callback(this.currentViewport);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Get current viewport information
   */
  public getViewport(): ViewportInfo {
    return { ...this.currentViewport };
  }

  /**
   * Check if current viewport matches media query
   */
  public matches(query: string): boolean {
    return window.matchMedia(query).matches;
  }

  /**
   * Get responsive value based on current breakpoint
   */
  public getResponsiveValue<T>(
    values: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', T>>
  ): T | undefined {
    const breakpoint = this.currentViewport.breakpoint;

    // Try current breakpoint first
    if (values[breakpoint] !== undefined) {
      return values[breakpoint];
    }

    // Fall back to smaller breakpoints
    const breakpoints: ('xs' | 'sm' | 'md' | 'lg' | 'xl')[] = [
      'xs',
      'sm',
      'md',
      'lg',
      'xl',
    ];
    const currentIndex = breakpoints.indexOf(breakpoint);

    for (let i = currentIndex - 1; i >= 0; i--) {
      if (values[breakpoints[i]] !== undefined) {
        return values[breakpoints[i]];
      }
    }

    return undefined;
  }

  /**
   * Apply safe area insets to element
   */
  public applySafeAreaInsets(
    element: HTMLElement,
    sides: ('top' | 'right' | 'bottom' | 'left')[] = [
      'top',
      'right',
      'bottom',
      'left',
    ]
  ): void {
    const insets = this.currentViewport.safeAreaInsets;

    sides.forEach((side) => {
      const value = insets[side];
      if (value > 0) {
        element.style.setProperty(`padding-${side}`, `${value}px`);
      }
    });
  }

  /**
   * Get optimal image sizes for current viewport
   */
  public getOptimalImageSizes(): string {
    // const { width, devicePixelRatio } = this.currentViewport; // unused

    const sizes = {
      xs: '100vw',
      sm: '100vw',
      md: '50vw',
      lg: '33vw',
      xl: '25vw',
    };

    return `(max-width: ${this.config.breakpoints.sm}px) ${sizes.xs}, 
            (max-width: ${this.config.breakpoints.md}px) ${sizes.sm},
            (max-width: ${this.config.breakpoints.lg}px) ${sizes.md},
            (max-width: ${this.config.breakpoints.xl}px) ${sizes.lg},
            ${sizes.xl}`;
  }

  /**
   * Enable CSS custom properties for current viewport
   */
  public updateCSSCustomProperties(): void {
    const { width, height, isMobile, isTablet, isDesktop, safeAreaInsets } =
      this.currentViewport;

    document.documentElement.style.setProperty(
      '--viewport-width',
      `${width}px`
    );
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${height}px`
    );
    document.documentElement.style.setProperty(
      '--is-mobile',
      isMobile ? '1' : '0'
    );
    document.documentElement.style.setProperty(
      '--is-tablet',
      isTablet ? '1' : '0'
    );
    document.documentElement.style.setProperty(
      '--is-desktop',
      isDesktop ? '1' : '0'
    );

    // Safe area insets
    document.documentElement.style.setProperty(
      '--safe-area-top',
      `${safeAreaInsets.top}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-right',
      `${safeAreaInsets.right}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-bottom',
      `${safeAreaInsets.bottom}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-left',
      `${safeAreaInsets.left}px`
    );
  }

  /**
   * Clean up listeners and observers
   */
  public destroy(): void {
    window.removeEventListener('resize', this.handleResize.bind(this));
    window.removeEventListener(
      'orientationchange',
      this.handleOrientationChange.bind(this)
    );

    if (this.visualViewport) {
      this.visualViewport.removeEventListener(
        'resize',
        this.handleViewportResize.bind(this)
      );
      this.visualViewport.removeEventListener(
        'scroll',
        this.handleViewportScroll.bind(this)
      );
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.callbacks.clear();
  }
}

// Utility functions for responsive behavior
export class ResponsiveUtils {
  /**
   * Create a responsive element that adapts to viewport changes
   */
  static createResponsiveElement(
    element: HTMLElement,
    viewportDetector: ViewportDetector,
    responsiveConfig: {
      classes?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string>>;
      styles?: Partial<
        Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', Partial<CSSStyleDeclaration>>
      >;
    }
  ): () => void {
    const updateElement = (viewport: ViewportInfo) => {
      // Remove all breakpoint classes
      element.className = element.className.replace(
        /\b(xs|sm|md|lg|xl)-\w+/g,
        ''
      );

      // Add current breakpoint class
      if (responsiveConfig.classes?.[viewport.breakpoint]) {
        element.classList.add(responsiveConfig.classes[viewport.breakpoint]!);
      }

      // Apply responsive styles
      if (responsiveConfig.styles?.[viewport.breakpoint]) {
        const styles = responsiveConfig.styles[viewport.breakpoint]!;
        Object.assign(element.style, styles);
      }
    };

    return viewportDetector.subscribe(updateElement);
  }

  /**
   * Optimize element for current device type
   */
  static optimizeForDevice(element: HTMLElement, viewport: ViewportInfo): void {
    if (viewport.isMobile) {
      // Mobile optimizations
      element.style.touchAction = 'manipulation';
      (
        element.style as CSSStyleDeclaration & {
          webkitTapHighlightColor?: string;
        }
      ).webkitTapHighlightColor = 'transparent';
      element.style.userSelect = 'none';

      // Larger touch targets
      if (element.tagName === 'BUTTON' || element.tagName === 'A') {
        element.style.minHeight = '44px';
        element.style.minWidth = '44px';
      }
    }

    if (viewport.devicePixelRatio > 1) {
      // High DPI optimizations
      element.style.imageRendering = 'crisp-edges';
    }
  }
}

// Global viewport detector instance
let globalViewportDetector: ViewportDetector | null = null;

/**
 * Get or create global viewport detector instance
 */
export function getViewportDetector(
  config?: Partial<ResponsiveConfig>
): ViewportDetector {
  if (!globalViewportDetector) {
    globalViewportDetector = new ViewportDetector(config);
    globalViewportDetector.updateCSSCustomProperties();
    globalViewportDetector.subscribe(() => {
      globalViewportDetector!.updateCSSCustomProperties();
    });
  }
  return globalViewportDetector;
}
